import { ClipboardList } from 'lucide-react'
import { relativeTime } from '@/lib/format'
import {
  formatToolValue,
  isToolError,
  pickString,
  plural,
  readToolState,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const completedIcon = (
  <ClipboardList className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
)

function auditLogsFromResult(result: any): Array<Record<string, unknown>> {
  if (Array.isArray(result?.logs)) return result.logs
  return []
}

function formatAuditAction(action: unknown): string {
  const value = formatToolValue(action)
  return value ? value.replace(/\./g, ' ') : 'Audit event'
}

function formatAuditTime(value: unknown): string | null {
  const raw = formatToolValue(value)
  if (!raw) return null
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return raw
  return relativeTime(date, { suffix: true })
}

function formatAuditActor(log: Record<string, unknown>): string | null {
  return pickString(log.actorName, log.actorType)
}

function formatAuditTarget(log: Record<string, unknown>): string | null {
  return pickString(log.targetName, log.targetType, log.targetId)
}

function joinDetail(...values: unknown[]): string | null {
  const parts = values
    .map((value) => formatToolValue(value))
    .filter((value): value is string => Boolean(value))
  return parts.length > 0 ? parts.join(' · ') : null
}

function auditActionFilter(args: Record<string, unknown> | undefined) {
  const action = formatToolValue(args?.action)
  if (action === 'getLogs' || action === 'getActivityFeed') {
    return formatToolValue(args?.actionName)
  }
  return action
}

function AuditLogsPreview({
  logs,
  emptyLabel,
}: {
  logs: Array<Record<string, unknown>>
  emptyLabel: string
}) {
  return (
    <ToolListPreview
      emptyLabel={emptyLabel}
      items={logs.map((log) => ({
        id: pickString(log.id),
        title: formatAuditAction(log.action),
        subtitle: formatAuditActor(log),
        meta: [
          log.category,
          log.result,
          formatAuditTime(log.timestamp),
          formatAuditTarget(log),
        ],
      }))}
    />
  )
}

export function AuditGetActivityFeedToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const logs = auditLogsFromResult(state.result)
  const count =
    typeof state.result?.count === 'number' ? state.result.count : logs.length
  const failed = state.isError || isToolError(state)

  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading activity feed...'
        completedLabel={
          count !== null
            ? `${plural(count, 'activity event')} loaded`
            : 'Activity feed loaded'
        }
        loadingDetail={joinDetail(state.args?.targetType, state.args?.targetId)}
        completedDetail={
          failed
            ? joinDetail(
                state.result?.targetType,
                state.args?.targetType,
                state.args?.targetId
              )
            : null
        }
        completedIcon={completedIcon}
      />
      {state.isComplete && !failed ? (
        <AuditLogsPreview logs={logs} emptyLabel='No activity found' />
      ) : null}
    </>
  )
}

export function AuditGetLogsToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const logs = auditLogsFromResult(state.result)
  const total =
    typeof state.result?.total === 'number' ? state.result.total : logs.length
  const failed = state.isError || isToolError(state)

  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Searching audit logs...'
        completedLabel={
          total !== null
            ? `Found ${plural(total, 'audit log')}`
            : 'Audit logs loaded'
        }
        loadingDetail={pickString(
          state.args?.category,
          auditActionFilter(state.args),
          state.args?.search,
          state.args?.targetId
        )}
        completedDetail={
          failed
            ? pickString(
                state.args?.category,
                auditActionFilter(state.args),
                state.args?.search,
                state.args?.targetId
              )
            : state.result?.hasMore
              ? `${logs.length} shown`
              : null
        }
        completedIcon={completedIcon}
      />
      {state.isComplete && !failed ? (
        <AuditLogsPreview logs={logs} emptyLabel='No audit logs found' />
      ) : null}
    </>
  )
}

export function AuditToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  if (state.args?.action === 'getActivityFeed') {
    return <AuditGetActivityFeedToolMessage part={part} />
  }
  if (state.args?.action === 'getLogs') {
    return <AuditGetLogsToolMessage part={part} />
  }
  return <AuditGetLogsToolMessage part={part} />
}
